import { CSSProperties } from 'react';

import { isDateRange } from '@/lib/date';
import { getThemeColors } from '@/lib/theme-colors';

import { notNil } from '@/lib/utils';

import {
  FormattingRule,
  ColorScalePoint,
  ColorScaleRule,
  SingleColorRule,
  TextFormat,
  DataTableRecord,
} from '../types';

export type ColumnStats = { min: number; max: number; sorted: number[] };

const DateRuleValueRegex = /^\d{4}-\d{2}-\d{2}$/;

export const evaluateRule = (rule: SingleColorRule, value: unknown): boolean => {
  const operator = rule.condition.operator;

  if (operator === 'isnull') {
    return value === null || value === undefined || value === '';
  }

  if (operator === 'isnotnull') {
    return value !== null && value !== undefined && value !== '';
  }

  if (rule.condition.value === undefined || rule.condition.value === '') {
    return false;
  }

  const ruleValue = rule.condition.value;

  // Date range check for == (cell date within range) and != (outside range)
  if (isDateRange(ruleValue)) {
    const cellDateStr = String(value).slice(0, 10);
    if (operator === '==') {
      return cellDateStr >= ruleValue.from && cellDateStr <= ruleValue.to;
    }
    if (operator === '!=') {
      return cellDateStr < ruleValue.from || cellDateStr > ruleValue.to;
    }
    return false;
  }

  if (operator === 'icontains') {
    return (
      typeof value === 'string' && value.toLowerCase().includes(String(ruleValue).toLowerCase())
    );
  }

  if (operator === 'noticontains') {
    return (
      typeof value === 'string' && !value.toLowerCase().includes(String(ruleValue).toLowerCase())
    );
  }

  if (operator === 'in' || operator === 'notin') {
    // Support both native array (new) and comma-string (legacy)
    const options = Array.isArray(ruleValue)
      ? ruleValue.map((v) => String(v).trim())
      : String(ruleValue)
          .split(',')
          .map((v) => v.trim());
    const includes = options.some((v) => String(value) === v);
    return operator === 'in' ? includes : !includes;
  }

  if (operator === 'arrcontains') {
    return Array.isArray(value) && value.some((v) => String(v) === String(ruleValue));
  }

  // Date-aware comparison: ruleValue stored as YYYY-MM-DD, cell value may be a full ISO datetime
  const isDateRuleValue = DateRuleValueRegex.test(String(ruleValue));
  if (isDateRuleValue) {
    const dateValue = new Date(String(value));
    const dateRuleValue = new Date(String(ruleValue));
    if (!isNaN(dateValue.getTime()) && !isNaN(dateRuleValue.getTime())) {
      const cellDateStr = String(value).slice(0, 10);
      const ruleDateStr = String(ruleValue);
      if (operator === '==') {
        return cellDateStr === ruleDateStr;
      }
      if (operator === '!=') {
        return cellDateStr !== ruleDateStr;
      }
      if (operator === '>') {
        return cellDateStr > ruleDateStr;
      }
      if (operator === '>=') {
        return cellDateStr >= ruleDateStr;
      }
      if (operator === '<') {
        return cellDateStr < ruleDateStr;
      }
      if (operator === '<=') {
        return cellDateStr <= ruleDateStr;
      }
    }
  }

  if (operator === '==' || operator === '!=') {
    const isEqual = String(value) === String(ruleValue);
    return operator === '==' ? isEqual : !isEqual;
  }

  const numericValue = Number(value);
  const numericRuleValue = Number(ruleValue);

  if (isNaN(numericValue) || isNaN(numericRuleValue)) {
    return false;
  }

  if (operator === '>') {
    return numericValue > numericRuleValue;
  }
  if (operator === '>=') {
    return numericValue >= numericRuleValue;
  }
  if (operator === '<') {
    return numericValue < numericRuleValue;
  }
  if (operator === '<=') {
    return numericValue <= numericRuleValue;
  }

  return false;
};

export const textStyleToCSS = (text: TextFormat | undefined): CSSProperties => {
  const textDecorations = [
    text?.underline === true ? 'underline' : '',
    text?.strikethrough === true ? 'line-through' : '',
  ].filter(Boolean);
  return {
    ...(text?.color !== undefined ? { color: text.color } : {}),
    ...(text?.bold === true ? { fontWeight: 'bold' } : {}),
    ...(text?.italic === true ? { fontStyle: 'italic' } : {}),
    ...(textDecorations.length > 0 ? { textDecoration: textDecorations.join(' ') } : {}),
  };
};

const getLuminance = (color: string): number => {
  const r = parseInt(color.slice(1, 3), 16) / 255;
  const g = parseInt(color.slice(3, 5), 16) / 255;
  const b = parseInt(color.slice(5, 7), 16) / 255;
  const toLinear = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
};

const getContrastRatio = (a: string, b: string): number => {
  const aL = getLuminance(a);
  const bL = getLuminance(b);
  return aL > bL ? (aL + 0.05) / (bL + 0.05) : (bL + 0.05) / (aL + 0.05);
};

export const getAutoTextColor = (bgColor: string | undefined): string => {
  const theme = getThemeColors();
  if (hasNoColor(bgColor)) {
    return theme.textDefault;
  }
  return getContrastRatio(bgColor ?? '', theme.neutral0) >=
    getContrastRatio(bgColor ?? '', theme.neutral100)
    ? theme.neutral0
    : theme.neutral100;
};

const ruleToStyle = (rule: SingleColorRule): CSSProperties => {
  const { text, bg } = rule;
  const hasBg = bg.color !== undefined && bg.color !== '';
  const hasTextColor = text?.color !== undefined && text.color !== '';
  const autoColor = hasBg && !hasTextColor ? getAutoTextColor(bg.color as string) : undefined;
  return {
    ...(hasBg ? { backgroundColor: bg.color } : {}),
    ...textStyleToCSS(text),
    ...(autoColor !== undefined ? { color: autoColor } : {}),
  };
};

const interpolateHex = (c1: string, c2: string, t: number): string => {
  const r1 = parseInt(c1.slice(1, 3), 16);
  const g1 = parseInt(c1.slice(3, 5), 16);
  const b1 = parseInt(c1.slice(5, 7), 16);
  const r2 = parseInt(c2.slice(1, 3), 16);
  const g2 = parseInt(c2.slice(3, 5), 16);
  const b2 = parseInt(c2.slice(5, 7), 16);
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

const resolvePointValue = (point: ColorScalePoint, stats: ColumnStats): number => {
  if (point.type === 'number') {
    return point.value;
  }
  if (point.type === 'percentage') {
    return stats.min + (point.value / 100) * (stats.max - stats.min);
  }
  // percentile
  const { sorted } = stats;
  if (sorted.length === 0) {
    return point.value;
  }
  const idx = Math.min(Math.floor((point.value / 100) * sorted.length), sorted.length - 1);
  return sorted[idx];
};

const interpolateAtStops = (colors: string[], stops: number[], value: number): string => {
  if (value <= stops[0]) {
    return colors[0];
  }
  if (value >= stops[stops.length - 1]) {
    return colors[colors.length - 1];
  }
  let segIdx = 0;
  for (let i = 0; i < stops.length - 1; i++) {
    if (value >= stops[i] && value <= stops[i + 1]) {
      segIdx = i;
      break;
    }
  }
  const denominator = stops[segIdx + 1] - stops[segIdx];
  if (Math.abs(denominator) < Number.EPSILON) {
    return colors[segIdx];
  }
  const t = Math.max(0, Math.min(1, (value - stops[segIdx]) / denominator));
  return interpolateHex(colors[segIdx], colors[segIdx + 1], t);
};

const getColorScaleStyle = (
  rule: ColorScaleRule,
  value: unknown,
  columnStats?: ColumnStats,
): CSSProperties | undefined => {
  const numValue = Number(value);
  if (isNaN(numValue)) {
    return undefined;
  }

  const { text } = rule;

  if (rule.minPoint === undefined || rule.maxPoint === undefined) {
    return undefined;
  }

  const stats = columnStats ?? { min: 0, max: 0, sorted: [] };
  const minVal = resolvePointValue(rule.minPoint, stats);
  const maxVal = resolvePointValue(rule.maxPoint, stats);
  if (minVal >= maxVal) {
    return undefined;
  }

  const mid =
    rule.midPoint !== undefined && rule.midPoint.type !== 'none' ? rule.midPoint : undefined;

  const colors =
    mid !== undefined
      ? [rule.minPoint.color, mid.color, rule.maxPoint.color]
      : [rule.minPoint.color, rule.maxPoint.color];
  const stops =
    mid !== undefined ? [minVal, resolvePointValue(mid, stats), maxVal] : [minVal, maxVal];

  const bgColor = interpolateAtStops(colors, stops, numValue);
  const hasTextColor = text?.color !== undefined && text.color !== '';
  const autoColor = !hasTextColor ? getAutoTextColor(bgColor) : undefined;
  return {
    backgroundColor: bgColor,
    ...textStyleToCSS(text),
    ...(autoColor !== undefined ? { color: autoColor } : {}),
  };
};

export const isColorScaleRule = (rule: FormattingRule): rule is ColorScaleRule =>
  'type' in rule && rule.type === 'colorScale';

const getRuleStyle = (
  rule: FormattingRule,
  value: unknown,
  columnStats?: Record<string, ColumnStats>,
): CSSProperties | undefined => {
  if (isColorScaleRule(rule)) {
    return getColorScaleStyle(rule, value, columnStats?.[rule.fieldKey]);
  }
  if ((rule as SingleColorRule).condition === undefined) {
    return undefined;
  }
  if (!evaluateRule(rule, value)) {
    return undefined;
  }
  const style = ruleToStyle(rule);
  return { ...style, '--text-link': style.color } as CSSProperties;
};

export const getCellStyle = (
  rules: FormattingRule[],
  fieldKey: string,
  value: unknown,
  columnStats?: Record<string, ColumnStats>,
): CSSProperties | undefined =>
  rules
    .filter((rule) => rule.fieldKey === fieldKey)
    .map((rule) => getRuleStyle(rule, value, columnStats))
    .findLast(notNil);

export const getRowStyle = (
  rules: FormattingRule[],
  record: DataTableRecord,
  columnStats?: Record<string, ColumnStats>,
): CSSProperties | undefined =>
  rules
    .filter((rule) => rule.applyToRow === true)
    .map((rule) => getRuleStyle(rule, record[rule.fieldKey], columnStats))
    .findLast(notNil);

// Validates HEX color format: #RGB, #RRGGBB, or #RRGGBBAA
export const isValidHexColor = (color: string): boolean => {
  return /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})$/.test(color);
};

export const ensureHexPrefix = (color: string): string => {
  return hasNoColor(color) ? '' : `#${color.replace(/^#+/g, '')}`;
};

export const hasNoColor = (color: string | undefined): boolean => {
  return color === '' || color === undefined;
};
